import * as React from 'react';
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface Announcement {
  message: string;
  politeness: 'polite' | 'assertive';
  timestamp: number;
}

interface ScreenReaderAnnounceContextType {
  announce: (message: string, politeness?: 'polite' | 'assertive') => void;
  announcePolite: (message: string) => void;
  announceAssertive: (message: string) => void;
  currentAnnouncement: Announcement | null;
}

const ScreenReaderAnnounceContext = createContext<ScreenReaderAnnounceContextType | undefined>(undefined);

interface ScreenReaderAnnounceProviderProps {
  children: ReactNode;
}

export function ScreenReaderAnnounceProvider({ children }: ScreenReaderAnnounceProviderProps) {
  const [currentAnnouncement, setCurrentAnnouncement] = useState<Announcement | null>(null);

  const announce = useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    // Use timestamp to ensure unique announcements trigger re-render
    setCurrentAnnouncement({
      message,
      politeness,
      timestamp: Date.now(),
    });

    // Clear after announcement is read (5 seconds for screen readers to catch it)
    setTimeout(() => {
      setCurrentAnnouncement(null);
    }, 5000);
  }, []);

  const announcePolite = useCallback((message: string) => {
    announce(message, 'polite');
  }, [announce]);

  const announceAssertive = useCallback((message: string) => {
    announce(message, 'assertive');
  }, [announce]);

  return (
    <ScreenReaderAnnounceContext.Provider 
      value={{ announce, announcePolite, announceAssertive, currentAnnouncement }}
    >
      {children}
      {/* Global live regions for screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {currentAnnouncement?.politeness === 'polite' ? currentAnnouncement.message : ''}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {currentAnnouncement?.politeness === 'assertive' ? currentAnnouncement.message : ''}
      </div>
    </ScreenReaderAnnounceContext.Provider>
  );
}

export function useScreenReaderAnnounce() {
  const context = useContext(ScreenReaderAnnounceContext);
  if (context === undefined) {
    throw new Error('useScreenReaderAnnounce must be used within a ScreenReaderAnnounceProvider');
  }
  return context;
}

// Optional hook for components that may or may not be within the provider
export function useOptionalScreenReaderAnnounce() {
  const context = useContext(ScreenReaderAnnounceContext);
  return context ?? {
    announce: () => {},
    announcePolite: () => {},
    announceAssertive: () => {},
    currentAnnouncement: null,
  };
}
